import React, { useState, useEffect, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from './context/AuthContext';
import Navbar from './components/navbar';
import Footer from './components/footer';
import './Dashboard.css';
import './Responsive.css';
import { uploadVideo, downloadVideo, deleteVideoFile, copyVideo } from './services/storeageFB';
import { createComercial, getComerciales, updateComercial, deleteComercial, subscribeToComerciales } from './services/services';
import whatsappService from './services/whatsappService';
import AnalyticsPanel from './components/AnalyticsPanel';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableRow = ({ comercial, index, selectedAnalyticsIds, toggleAnalyticsSelection, isSelected, toggleSelection, handleDownload, handleDuplicate, handleDeleteRequest, handleRequestDelete, handleStatusToggle, isAdmin, canManageStatus, isSelectionMode }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: comercial.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 999 : 'auto',
    };

    return (
        <div ref={setNodeRef} style={style} className={`video-row-card compacted ${isDragging ? 'is-dragging' : ''} ${selectedAnalyticsIds.has(comercial.id) ? 'selected-for-analytics' : ''} ${isSelected ? 'is-selected' : ''}`}>

            {/* BULK SELECTION CHECKBOX */}
            {isSelectionMode && (
                <div className="selection-checkbox-container">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(comercial.id)}
                        className="row-selection-checkbox"
                    />
                </div>
            )}

            {/* Side Control Bar (Grip + Rank) */}
            <div className="row-side-grip">
                <div className="drag-handle" {...attributes} {...listeners} title="Arrastrar para reordenar">
                    <div className="drag-dotnet"></div>
                </div>
                <div className="rank-badge">
                    <span className="rank-number">{index + 1}</span>
                </div>
            </div>

            {/* Checkbox for Analytics (Floating overlay) */}
            <div className="analytics-checkbox-wrapper" title="Seleccionar para comparar métricas">
                <input
                    type="checkbox"
                    checked={selectedAnalyticsIds.has(comercial.id)}
                    onChange={() => toggleAnalyticsSelection(comercial.id)}
                    className="analytics-checkbox"
                />
            </div>

            {/* LEFT: Video Thumbnail */}
            <div className="row-thumbnail-section">
                <div className="video-thumbnail">
                    {(comercial.videoUrl || comercial.videoPreview) ? (
                        <>
                            {comercial.thumbnail && (
                                <img
                                    src={comercial.thumbnail}
                                    alt="Previsualización"
                                    className="thumb-img"
                                />
                            )}
                            <video
                                src={comercial.videoUrl || comercial.videoPreview}
                                className="thumb-video"
                                muted
                                onMouseOver={e => e.target.play().catch(() => { })}
                                onMouseOut={e => {
                                    e.target.pause();
                                    e.target.currentTime = 0;
                                }}
                            />
                        </>
                    ) : (
                        <div className="no-video-placeholder">Sin Video</div>
                    )}
                    <span className="duration-badge">{comercial.duracion}s</span>
                    <span className="orientation-badge">{comercial.orientacion === 'Vertical' ? '📱' : '💻'}</span>
                </div>
            </div>

            {/* Right: Content Section - Compact Layout */}
            <div className="row-content-section">

                {/* Header: Title and Actions */}
                <div className="row-header">
                    <h3 className="video-title">
                        {comercial.titulo}
                        {isAdmin && comercial.solicitudBaja && (
                            <span className="removal-request-badge" title="El usuario de marketing solicitó dar de baja este video">
                                ⚠️ Solicitud de Baja
                            </span>
                        )}
                    </h3>
                    <div className="row-actions">
                        {isAdmin && canManageStatus && (
                            <button
                                className={`btn-icon-action status-toggle ${comercial.estado}`}
                                onClick={() => handleStatusToggle(comercial.id)}
                                title={comercial.estado === 'online' ? 'Desactivar' : 'Activar'}
                            >
                                <span className="btn-icon-visual"></span>
                                <span className="btn-label-hover">
                                    {comercial.estado === 'online' ? 'Desactivar' : 'Activar'}
                                </span>
                            </button>
                        )}

                        {isAdmin && (
                            <button
                                className="btn-icon-action duplicate"
                                onClick={() => handleDuplicate(comercial)}
                                title="Duplicar Video"
                            >
                                <span className="btn-icon-visual">📋</span>
                                <span className="btn-label-hover">Duplicar</span>
                            </button>
                        )}

                        <button
                            className="btn-icon-action download"
                            onClick={() => handleDownload(comercial)}
                            title="Descargar"
                        >
                            <span className="btn-icon-visual">⬇</span>
                            <span className="btn-label-hover">Descargar</span>
                        </button>

                        {/* Removal logic per role */}
                        {isAdmin ? (
                            <button
                                className="btn-icon-action delete"
                                onClick={() => handleDeleteRequest(comercial)}
                                title="Eliminar permanentemente"
                            >
                                <span className="btn-icon-visual">🗑️</span>
                                <span className="btn-label-hover">Eliminar</span>
                            </button>
                        ) : (
                            !comercial.solicitudBaja && (
                                <button
                                    className="btn-action btn-request-removal"
                                    onClick={() => handleRequestDelete(comercial)}
                                    title="Solicitar baja del sistema"
                                >
                                    🔻 Dar de Baja
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* Main Info Grid (Excel Style) */}
                <div className="row-info-grid">

                    {/* Column 1: Segmentation */}
                    <div className="info-cell">
                        <span className="cell-label">SEGMENTACIÓN</span>
                        <div className="cell-value">
                            <span className="value-icon">
                                {comercial.segmentacion && typeof comercial.segmentacion === 'object' ?
                                    (comercial.segmentacion.tipo === 'Nacional' ? '🇪🇨' : '📍') : '🌐'}
                            </span>
                            <span className="value-text">
                                {comercial.segmentacion && typeof comercial.segmentacion === 'object' ? comercial.segmentacion.tipo : (comercial.segmentacion || 'Nacional')}
                            </span>
                        </div>
                        {comercial.segmentacion && typeof comercial.segmentacion === 'object' && comercial.segmentacion.tipo === 'Por Zona' && (
                            <div className="cell-subtext">
                                {comercial.segmentacion.region}
                                {Array.isArray(comercial.segmentacion.ciudad) && comercial.segmentacion.ciudad.length > 0 && ` • ${comercial.segmentacion.ciudad.length} Ciudades`}
                            </div>
                        )}
                    </div>

                    {/* Column 2: Schedule */}
                    <div className="info-cell schedule-cell">
                        <span className="cell-label">PROGRAMACIÓN</span>
                        <div className="cell-value">
                            📅 {(new Date(comercial.scheduleStart)).toLocaleDateString()} - {(new Date(comercial.scheduleEnd)).toLocaleDateString()}
                        </div>
                        <div className="cell-subtext">
                            ⏰ {comercial.dailyStartTime || '08:00'} a {comercial.dailyEndTime || '20:00'}
                        </div>
                    </div>

                    {/* Column 3: Detailed Segmentation (Cities/Stores) */}
                    <div className="info-cell segmentation-details-cell">
                        <span className="cell-label">CIUDADES / TIENDAS</span>
                        <div className="cell-value-scroll">
                            {comercial.segmentacion && comercial.segmentacion.tipo === 'Nacional' ? (
                                <span className="value-text highlight">Cobertura Nacional 🇪🇨</span>
                            ) : (
                                <div className="segmentation-badges">
                                    {Array.isArray(comercial.segmentacion?.ciudad) && comercial.segmentacion.ciudad.length > 0 ? (
                                        comercial.segmentacion.ciudad.map(c => (
                                            <span key={c} className="mini-geo-badge city">{c}</span>
                                        ))
                                    ) : (
                                        <span className="no-data">Sin Ciudades</span>
                                    )}
                                    {Array.isArray(comercial.segmentacion?.tienda) && comercial.segmentacion.tienda.length > 0 && (
                                        <span className="mini-geo-badge store">+{comercial.segmentacion.tienda.length} Tiendas</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Column 4: Analytics (Simpler for list) */}
                    <div className="info-cell analytics-cell">
                        <span className="cell-label">CICLOS ESTIMADOS</span>
                        <span className="cell-value impact">
                            {comercial.duracion > 0 ? Math.floor((3600 * 12) / comercial.duracion).toLocaleString() : 0}
                        </span>
                        <span className="cell-sub">
                            Reps/Día (12h)
                        </span>
                        <span className="cell-sub">
                            {comercial.duracion > 0 ? (Math.floor((3600 * 12 * 30) / comercial.duracion)).toLocaleString() : 0} / mes
                        </span>
                    </div>
                    {/* Column 5 (Was Footer): Status & User */}
                    <div className="info-cell status-cell">
                        <span className="cell-label">ESTADO / USUARIO</span>
                        <div className="cell-value">
                            <div className="card-status">
                                {comercial.status === 'uploading' ? (
                                    <span className="badge badge-warning">⏳ Subiendo...</span>
                                ) : comercial.status === 'saving' ? (
                                    <span className="badge badge-info pulse">⌛ Guardando...</span>
                                ) : comercial.status === 'error_upload' ? (
                                    <span className="badge badge-danger">❌ Error</span>
                                ) : comercial.status === 'error_db' ? (
                                    <span className="badge badge-danger">⚠️ Error DB</span>
                                ) : comercial.estado === 'online' ? (
                                    <span className="badge badge-online">✅ ONLINE</span>
                                ) : (
                                    <span className="badge badge-offline">⭕ OFFLINE</span>
                                )}
                            </div>
                        </div>
                        <div className="cell-subtext user-info">
                            👤 {comercial.usuario}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

// --- GLOBAL PREVIEW COMPONENT ---
const GlobalPreviewOverlay = ({ videos, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const videoRef = React.useRef(null);

    const currentVideo = videos[currentIndex];

    // Effect: Handle Play/Pause and Speed whenever index or isPlaying changes
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = 3.0; // Enforce X3 Speed

            if (isPlaying) {
                videoRef.current.play().catch(e => console.error("Auto-play blocked", e));
            } else {
                videoRef.current.pause(); // Fix: Explicit pause
            }
        }
    }, [currentIndex, isPlaying]);

    // Handle video end -> Next or Close
    const handleEnded = () => {
        if (currentIndex < videos.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            console.log("[GlobalPreview] Playlist finished. Looping to start...");
            setCurrentIndex(0); // LOOP playlist automatically
        }
    };

    // Handle 404 / Loading errors
    const handleVideoError = (e) => {
        console.warn("[GlobalPreview] Error loading video at index", currentIndex, ". Skipping...");
        if (currentIndex < videos.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            setIsPlaying(false);
        }
    };

    const handleNext = () => {
        if (currentIndex < videos.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setIsPlaying(true); // Ensure play continues
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setIsPlaying(true);
        }
    };

    if (!currentVideo) return null;

    return (
        <div className="global-preview-overlay">
            {/* Close Button */}
            <button className="preview-close-btn" onClick={onClose} title="Cerrar">✕</button>

            {/* Info Overlay */}
            <div className="preview-info-overlay">
                <h3>{currentVideo.titulo}</h3>
                <div className="preview-meta">
                    <span className="meta-badge">⚡ x3 Velocidad</span>
                    <span className="meta-badge">{currentIndex + 1} / {videos.length}</span>
                    <span className="meta-badge">{currentVideo.orientacion}</span>
                </div>
            </div>

            {/* Video Container */}
            <div className="video-wrapper">
                <video
                    ref={videoRef}
                    src={currentVideo.videoUrl || currentVideo.videoPreview}
                    onEnded={handleEnded}
                    onError={handleVideoError}
                    autoPlay
                    muted
                    playsInline
                    controls={false}
                    className="main-preview-video"
                    onClick={() => setIsPlaying(!isPlaying)}
                />
            </div>

            {/* Floating Controls Bar */}
            <div className="preview-controls-bar">
                <button
                    className="control-btn"
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    title="Anterior"
                >
                    ⏮
                </button>

                <button
                    className="control-btn play-pause"
                    onClick={() => setIsPlaying(!isPlaying)}
                    title={isPlaying ? "Pausar" : "Reproducir"}
                >
                    {isPlaying ? '⏸' : '▶'}
                </button>

                <button
                    className="control-btn"
                    onClick={handleNext}
                    disabled={currentIndex === videos.length - 1}
                    title="Siguiente"
                >
                    ⏭
                </button>
            </div>
        </div>
    );
};
const Dashboard = () => {
    const { user, isAdmin, canManageStatus, fbUser, isAuthorizedDomain } = useAuth();

    // Ref for Auto-Scroll
    const formRef = useRef(null);

    // Sensors for DND
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Form state
    const [formData, setFormData] = useState({
        titulo: '',
        segmentacion: {
            tipo: 'Nacional',
            region: [], // Changed to Array
            ciudad: [],
            tienda: []
        },
        orientacion: 'Horizontal',
        duracion: '',
        scheduleStart: new Date(),
        scheduleEnd: new Date(),
        dailyStartTime: '08:00',
        dailyEndTime: '20:00',
        comentarios: '',
        estado: 'offline',
        propietario: 'Jaher',
        archivo: null,
        videoPreview: null
    });
    const [showForm, setShowForm] = useState(false);
    const [videoToDelete, setVideoToDelete] = useState(null);
    const [showGlobalPreview, setShowGlobalPreview] = useState(false);

    const [uploadProgress, setUploadProgress] = useState(0);
    const [isSyncing, setIsSyncing] = useState(true);

    // List of registered commercials (Unified Cache for instant F5 loading)
    const [comerciales, setComerciales] = useState(() => {
        try {
            const saved = localStorage.getItem('cached_comerciales_v2');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    console.log("[Dashboard] 🟢 CACHE: Restoring", parsed.length, "items.");
                    return parsed;
                }
            }
        } catch (e) {
            console.error("[Dashboard] 🔴 CACHE: Load error:", e);
        }
        return [];
    });

    const [syncError, setSyncError] = useState(null);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [syncSource, setSyncSource] = useState('Buscando...');

    // --- BULK SELECTION LOGIC ---
    const [selectedVideos, setSelectedVideos] = useState(new Set());
    const [isSelectionMode, setIsSelectionMode] = useState(false); // NEW STATE

    const toggleVideoSelection = (id) => {
        setSelectedVideos(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAllVideos = () => {
        if (selectedVideos.size === filteredVideos.length && filteredVideos.length > 0) {
            setSelectedVideos(new Set());
        } else {
            setSelectedVideos(new Set(filteredVideos.map(v => v.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedVideos.size === 0) return;

        const confirmDelete = window.confirm(`¿Estás seguro de que deseas eliminar los ${selectedVideos.size} videos seleccionados? Esta acción no se puede deshacer.`);
        if (!confirmDelete) return;

        setIsSyncing(true);
        try {
            await Promise.all(Array.from(selectedVideos).map(id => deleteComercial(id)));
            setSelectedVideos(new Set());
        } catch (error) {
            console.error("Error en borrado masivo:", error);
            alert("Error al eliminar algunos videos.");
        } finally {
            setIsSyncing(false);
        }
    };

    React.useEffect(() => {
        console.log("[Dashboard] 🔄 Establishing real-time connection...");
        setIsSyncing(true);
        setSyncError(null);

        const unsubscribe = subscribeToComerciales((serverData, error, metadata) => {
            if (error) {
                console.error("[Dashboard] 🔴 CLOUD SYNC ERROR:", error.code, error.message);
                setSyncError(`${error.code}: ${error.message}`);
                setIsSyncing(false);
                return;
            }

            if (metadata) {
                setSyncSource(metadata.fromCache ? 'Cache Local (Offline)' : 'Servor (Live)');
                if (!metadata.fromCache) setLastSyncTime(new Date());
            }

            console.log(`[Dashboard] 📡 RECEIVED FROM SERVER: ${serverData.length} items.`);
            if (serverData.length === 0) {
                console.warn("[Dashboard] ⚠️ The server returned 0 videos. Check if the collection 'comerciales' exists in Firestore.");
            }
            setSyncError(null);

            setComerciales(prev => {
                // 1. Keep ALL local-only items (temp- ID) OR in-flight statuses
                const inFlight = prev.filter(c =>
                    c.id?.toString().startsWith('temp-') ||
                    c.id?.toString().startsWith('dup-') ||
                    c.status === 'uploading' ||
                    c.status === 'saving' ||
                    c.status === 'complete' ||
                    c.status?.startsWith('error_')
                );

                // 2. Identify which server items are already in our local list
                const serverIds = new Set(serverData.map(s => s.id));
                const serverTempIds = new Set(serverData.map(s => s.tempId).filter(Boolean));

                // 3. Keep inFlight ONLY if they haven't appeared on server yet
                const uniqueInFlight = inFlight.filter(local =>
                    !serverIds.has(local.id) && !serverTempIds.has(local.id)
                );

                const merged = [...serverData, ...uniqueInFlight];

                // 4. Sort newest first
                return merged.sort((a, b) => {
                    const dateA = new Date(a.fechaRegistro || 0).getTime();
                    const dateB = new Date(b.fechaRegistro || 0).getTime();
                    return dateB - dateA;
                });
            });
            setIsSyncing(false);
        });

        // Cleanup listener on unmount
        return () => {
            console.log("[Dashboard] 🛑 Cleaning up real-time listener.");
            unsubscribe();
        };
    }, []);

    // UNIFIED CACHE SYNC: Ensures F5 always shows current state instantly
    React.useEffect(() => {
        if (comerciales.length > 0) {
            const dataToCache = comerciales.slice(0, 100); // Keep last 100 for performance
            localStorage.setItem('cached_comerciales_v2', JSON.stringify(dataToCache));
        }
    }, [comerciales]);

    // AUTO-CLOSE FORM WHEN UPLOAD FINISHES
    React.useEffect(() => {
        if (uploadProgress >= 100 && showForm) {
            console.log("[Dashboard] Upload reached 100%. Auto-closing in 5s...");
            const timer = setTimeout(() => {
                setShowForm(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [uploadProgress, showForm]);

    // Filter states
    const [filters, setFilters] = useState({
        estado: 'all',
        orientacion: 'all'
    });

    // Analytics Selection State
    const [selectedAnalyticsIds, setSelectedAnalyticsIds] = useState(new Set());

    const toggleAnalyticsSelection = (id) => {
        setSelectedAnalyticsIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (max 500MB)
            const maxSize = 500 * 1024 * 1024; // 500MB in bytes
            if (file.size > maxSize) {
                alert('El archivo es demasiado grande. Tamaño máximo: 500MB');
                return;
            }

            // Create video preview URL
            const videoURL = URL.createObjectURL(file);

            // Verify video metadata (resolution and duration)
            const video = document.createElement('video');
            video.muted = true;
            video.playsInline = true;

            video.onloadedmetadata = () => {
                const duration = Math.round(video.duration);
                const width = video.videoWidth;
                const height = video.videoHeight;

                const isHorizontal = width > height;
                const isVertical = height > width;

                if (width < 300 || height < 300) {
                    alert(`Resolución no válida: ${width}x${height}. El video es demasiado pequeño.`);
                    e.target.value = '';
                    URL.revokeObjectURL(video.src);
                    return;
                }
            };

            video.oncanplay = () => {
                if (!video._hasStartedSeeking) {
                    video._hasStartedSeeking = true;
                    // Seek to 0.5s or half the video if shorter
                    video.currentTime = Math.min(0.5, video.duration / 2);
                }
            };

            video.onseeked = () => {
                // Small delay to ensure the frame is decoded and painted
                setTimeout(() => {
                    try {
                        const canvas = document.createElement('canvas');
                        // Target a smaller size for performance (Firestore limit is 1MB)
                        // A max width of 320px keeps the thumbnail useful but tiny in bytes
                        const targetWidth = 320;
                        const scaleFactor = targetWidth / video.videoWidth;
                        canvas.width = targetWidth;
                        canvas.height = video.videoHeight * scaleFactor;

                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                        // JPEG format with medium-high compression (0.7) for extreme lightness
                        const thumbnailData = canvas.toDataURL('image/jpeg', 0.7);
                        console.log("[Dashboard] 📸 Thumbnail resized to 320px. Base64 length:", thumbnailData.length);

                        setFormData(prev => ({
                            ...prev,
                            archivo: file,
                            fileName: file.name,
                            titulo: file.name.replace(/\.[^/.]+$/, ""),
                            duracion: Math.round(video.duration),
                            orientacion: (video.videoWidth > video.videoHeight) ? 'Horizontal' : 'Vertical',
                            videoPreview: URL.createObjectURL(file),
                            thumbnail: thumbnailData
                        }));
                    } catch (err) {
                        console.error("[Dashboard] Thumbnail capture failed:", err);
                    } finally {
                        URL.revokeObjectURL(video.src);
                    }
                }, 150); // Increased delay slightly for better decoding safety
            };

            video.onerror = () => {
                console.error("Error al cargar el video. Asegúrese de que es un archivo válido.");
                URL.revokeObjectURL(video.src);
            };

            video.src = URL.createObjectURL(file);


        }
    };



    const handleDuplicate = async (comercial) => {
        // Prevent multiple duplicates
        if (comercial.isDuplicating) {
            console.log("[Dashboard] ⚠️ Ya se está duplicando este video");
            return;
        }

        console.log("[Dashboard] 📋 Starting auto-duplication for:", comercial.titulo);

        // Mark as duplicating to prevent multiple clicks
        setComerciales(prev => prev.map(c =>
            c.id === comercial.id ? { ...c, isDuplicating: true } : c
        ));

        try {
            // 1. Prepare Data
            const tempId = `dup-${Date.now()}`;
            const newTitle = comercial.titulo; // REMOVED (Copia) as requested

            // Deep clone segmentation & enforce arrays for legacy data
            const seg = { ...comercial.segmentacion };
            seg.ciudad = Array.isArray(seg.ciudad) ? [...seg.ciudad] : (seg.ciudad ? [seg.ciudad] : []);
            seg.tienda = Array.isArray(seg.tienda) ? [...seg.tienda] : (seg.tienda ? [seg.tienda] : []);

            // 2. Optimistic Item (Instant UI Feedback)
            const optimisticComercial = {
                ...comercial,
                id: tempId,
                titulo: newTitle,
                segmentacion: seg,
                estado: 'offline', // Default to offline for safety
                fechaRegistro: new Date().toISOString(),
                scheduleStart: new Date().toISOString(),
                scheduleEnd: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString(),
                status: 'copying', // Show visually that it's copying
                isDuplicate: false, // REMOVED as requested
                solicitudBaja: false
            };

            // Add to list immediately
            setComerciales(prev => [optimisticComercial, ...prev]);
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // 3. Copy video file to new location in Firebase Storage
            console.log("[Dashboard] 📋 Copiando archivo de video...");
            const newVideoUrl = await copyVideo(comercial.videoUrl, `${Date.now()}_${comercial.fileName || 'copia.mp4'}`);
            console.log("[Dashboard] ✅ Video copiado exitosamente");

            // 4. Background Save with new video URL
            const finalData = {
                tempId: tempId, // Pass tempId for deduplication
                titulo: newTitle,
                segmentacion: seg,
                orientacion: comercial.orientacion,
                duracion: comercial.duracion,
                videoUrl: newVideoUrl, // Use NEW copied video URL
                thumbnail: comercial.thumbnail,
                fileSize: comercial.fileSize,
                fileName: `copia_${comercial.fileName || 'video.mp4'}`,
                usuario: user?.name || 'Usuario',
                fechaRegistro: new Date().toISOString(),
                scheduleStart: optimisticComercial.scheduleStart,
                scheduleEnd: optimisticComercial.scheduleEnd,
                comentarios: `Copia automática de: ${comercial.titulo}`,
                estado: 'offline',
                solicitudBaja: false,
                isDuplicate: false // REMOVED as requested
            };

            const docRef = await createComercial(finalData);
            console.log("[Dashboard] ✅ Duplication saved to DB. ID:", docRef.id);

            // Update ID in local state and remove duplicating flag from original
            setComerciales(prev => prev.map(c => {
                if (c.id === tempId) {
                    return { ...c, id: docRef.id, status: 'complete' };
                }
                if (c.id === comercial.id) {
                    return { ...c, isDuplicating: false };
                }
                return c;
            }));

        } catch (err) {
            console.error("[Dashboard] 🚨 Duplication Failed:", err);
            alert("Error al duplicar el video. Intente nuevamente.");

            // Rollback optimistic update and remove duplicating flag
            setComerciales(prev => prev.filter(c => c.id !== `dup-${Date.now()}`).map(c =>
                c.id === comercial.id ? { ...c, isDuplicating: false } : c
            ));
        }
    };

    const handleRequestDelete = async (comercial) => {
        try {
            // Optimistic Update
            setComerciales(prev => prev.map(c =>
                c.id === comercial.id ? { ...c, solicitudBaja: true } : c
            ));

            await updateComercial(comercial.id, { solicitudBaja: true });

            // WhatsApp Notification
            whatsappService.notifyDeactivation(comercial, user);
        } catch (error) {
            console.error("Error sending delete request:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!fbUser) {
            console.error("Firebase Auth not detected. Current fbUser:", fbUser);
            return;
        }

        // Allow submit if file is present OR if we are duplicating (videoUrl exists)
        if (!formData.archivo && !formData.videoUrl) {
            console.warn('Falta archivo para subir.');
            return;
        }

        console.log("[Dashboard] Submit process STARTED for:", formData.titulo);
        console.log("🔍 DEBUG: Verificando Firestore...");
        console.log("- fbUser existe?", !!fbUser);
        console.log("- FormData:", formData);

        try {
            // 1. Capture current form data and create a Tracking ID
            const capturedData = { ...formData };
            const tempId = `temp-${Date.now()}`;
            const isDuplication = !capturedData.archivo && capturedData.videoUrl;

            // 2. IMMEDIATE OPTIMISTIC UPDATE
            const optimisticComercial = {
                id: tempId,
                titulo: capturedData.titulo || (capturedData.archivo ? capturedData.archivo.name : 'Video Sin Título'),
                segmentacion: capturedData.segmentacion || { tipo: 'Nacional' },
                orientacion: capturedData.orientacion || 'Horizontal',
                duracion: capturedData.duracion || 0,
                videoUrl: capturedData.videoUrl, // Might be null if new upload
                videoPreview: capturedData.videoPreview,
                thumbnail: capturedData.thumbnail,
                status: 'uploading',
                progress: isDuplication ? 100 : 0, // Instant complete if duplicate
                estado: 'offline',
                fechaRegistro: new Date().toISOString(),
                usuario: user?.name || 'Usuario',
                solicitudBaja: false
            };

            setComerciales(prev => [optimisticComercial, ...prev]);
            setShowForm(false);

            // 3. BACKGROUND WORKER
            (async () => {
                try {
                    let downloadURL = capturedData.videoUrl;

                    // Phase A: Storage Upload (Only if new file)
                    if (!isDuplication) {
                        console.log("[Dashboard] 🚀 Phase A: Storage Upload...");
                        downloadURL = await uploadVideo(capturedData.archivo, (progress) => {
                            setUploadProgress(progress);
                            setComerciales(prev => prev.map(c =>
                                c.id === tempId ? { ...c, progress } : c
                            ));
                        });

                        // Critical Validation: Ensure we have a REAL URL from Firebase
                        if (!downloadURL || typeof downloadURL !== 'string' || !downloadURL.startsWith('http')) {
                            throw new Error("Invalid URL received from Storage. Upload might have failed silently.");
                        }
                    } else {
                        console.log("[Dashboard] 🚀 Phase A: Skipped (Duplication mode)");
                    }

                    // Phase B: Prep Firestore Data
                    const finalData = {
                        tempId: tempId,
                        titulo: capturedData.titulo || (capturedData.archivo ? capturedData.archivo.name : 'Sin Título'),
                        segmentacion: capturedData.segmentacion,
                        orientacion: capturedData.orientacion,
                        duracion: capturedData.duracion,
                        videoUrl: downloadURL,
                        thumbnail: capturedData.thumbnail || '',
                        fileSize: capturedData.fileSize || (capturedData.archivo ? (capturedData.archivo.size / (1024 * 1024)).toFixed(2) : '0'),
                        fileName: capturedData.fileName || (capturedData.archivo ? capturedData.archivo.name : 'duplicado.mp4'),
                        usuario: user?.name || 'Usuario',
                        fechaRegistro: new Date().toISOString(),
                        scheduleStart: capturedData.scheduleStart instanceof Date ? capturedData.scheduleStart.toISOString() : new Date().toISOString(),
                        scheduleEnd: capturedData.scheduleEnd instanceof Date ? capturedData.scheduleEnd.toISOString() : new Date().toISOString(),
                        comentarios: capturedData.comentarios || '',
                        estado: capturedData.estado || 'offline',
                        solicitudBaja: false
                    };

                    setComerciales(prev => prev.map(c =>
                        c.id === tempId ? { ...c, status: 'saving', videoUrl: downloadURL } : c
                    ));

                    // Phase C: Firestore Save
                    console.log("[Dashboard] 💾 Phase C: Firestore write STARTING...");
                    const dbStartTime = performance.now();

                    const docRef = await createComercial(finalData);

                    const dbEndTime = performance.now();
                    console.log(`[Dashboard] ✅ Phase C: FINISHED. Duration: ${(dbEndTime - dbStartTime).toFixed(2)}ms. ID: ${docRef.id}`);

                    // Success final state
                    setComerciales(prev => prev.map(c =>
                        c.id === tempId ? { ...c, id: docRef.id, status: 'complete' } : c
                    ));

                    // WhatsApp Notification
                    whatsappService.notifyNewVideo(finalData, user);

                    setTimeout(() => {
                        console.log("[Dashboard] 🔄 Sync handled by listener.");
                    }, 1500);

                } catch (err) {
                    console.error("[Dashboard] 🚨 FATAL BACKGROUND ERROR:", err);
                    setComerciales(prev => prev.map(c =>
                        c.id === tempId ? { ...c, status: (err.message.includes('storage') ? 'error_upload' : 'error_db') } : c
                    ));
                }
            })();

            // 4. Form Cleanup (Main Thread)
            setFormData({
                titulo: '',
                segmentacion: { tipo: 'Nacional', region: '', ciudad: [], tienda: [] },
                orientacion: 'Horizontal',
                duracion: '',
                scheduleStart: new Date(),
                scheduleEnd: new Date(new Date().setDate(new Date().getDate() + 7)),
                dailyStartTime: '08:00',
                dailyEndTime: '20:00',
                comentarios: '',
                estado: 'online',
                archivo: null,
                fileName: '',
                videoPreview: null,
                thumbnail: null,
                videoUrl: null
            });
            setUploadProgress(0);

        } catch (error) {
            console.error("Error submitting form:", error);
            alert("Hubo un error al iniciar la carga.");
        }
    };


    const handleUnsubscribe = () => {
        // Direct action without popups
        console.log(`Usuario ${user.name} solicitó baja.`);
        // In a real app, this would call an API
    };

    // Mock Data for Segmentation (Wiki based logic)
    const SEGMENTATION_DATA = {
        'Costa': {
            'Guayaquil': ['Tienda Centro', 'Tienda Norte', 'Mall del Sol'],
            'Machala': ['Tienda Principal', 'Piazza'],
            'Manta': ['Mall del Pacifico']
        },
        'Sierra': {
            'Quito': ['CCI', 'Recreo', 'Scala', 'Condado'],
            'Cuenca': ['Mall del Rio', 'Centro'],
            'Ambato': ['Mall de los Andes']
        },
        'Oriente': {
            'Puyo': ['Centro'],
            'Tena': ['Principal']
        }
    };

    const handleSegmentationChange = (field, value) => {
        setFormData(prev => {
            const newSeg = { ...prev.segmentacion };

            if (field === 'tipo') {
                newSeg.tipo = value;
                if (value === 'Nacional') {
                    newSeg.region = []; // Clear array
                    newSeg.ciudad = [];
                    newSeg.tienda = [];
                }
            }
            // Region handling is now done via handleRegionToggle

            return { ...prev, segmentacion: newSeg };
        });
    };

    const handleRegionToggle = (region) => {
        setFormData(prev => {
            const currentRegions = Array.isArray(prev.segmentacion.region) ? prev.segmentacion.region : [];
            const newRegions = currentRegions.includes(region)
                ? currentRegions.filter(r => r !== region)
                : [...currentRegions, region];

            // Filter cities/stores based on REMOVED regions
            // If a region is removed, remove its cities and stores
            let newCities = Array.isArray(prev.segmentacion.ciudad) ? [...prev.segmentacion.ciudad] : [];
            let newStores = Array.isArray(prev.segmentacion.tienda) ? [...prev.segmentacion.tienda] : [];

            if (currentRegions.includes(region)) {
                // Region REMOVED: clean up its children
                const regionCities = Object.keys(SEGMENTATION_DATA[region] || {});
                newCities = newCities.filter(c => !regionCities.includes(c));

                // For stores it's harder to reverse-map directly without iterating, 
                // but we can just filter stores that don't belong to any remaining city
                // Actually, let's keep it simple: if city is removed, store is removed by consequence?
                // No, store removal logic is usually tied to city. 
                // Let's filter stores to only keep those in remaining cities.
                // Re-calculating valid stores is safer.

                // ... logic above removes cities of that region.
                // Now remove stores of those removed cities.
                const regionCitiesRaw = SEGMENTATION_DATA[region] || {};
                Object.keys(regionCitiesRaw).forEach(city => {
                    const cityStores = regionCitiesRaw[city] || [];
                    newStores = newStores.filter(s => !cityStores.includes(s));
                });
            }

            return {
                ...prev,
                segmentacion: {
                    ...prev.segmentacion,
                    region: newRegions,
                    ciudad: newCities,
                    tienda: newStores
                }
            };
        });
    };


    const handleCityToggle = (city) => {
        setFormData(prev => {
            const currentCities = Array.isArray(prev.segmentacion.ciudad) ? prev.segmentacion.ciudad : [];
            const newCities = currentCities.includes(city)
                ? currentCities.filter(c => c !== city)
                : [...currentCities, city];

            // Auto-select stores for new cities, remove for desencies
            // Note: In multi-region, we must find which region contains this city to find its stores

            // Find region for this city
            let storesInRegion = {}; // Map of all stores for the city

            // Search in all available data (or just selected regions? All data is safer to find the city)
            for (const regionName of Object.keys(SEGMENTATION_DATA)) {
                if (SEGMENTATION_DATA[regionName][city]) {
                    storesInRegion = SEGMENTATION_DATA[regionName];
                    break;
                }
            }
            let newStores = Array.isArray(prev.segmentacion.tienda) ? [...prev.segmentacion.tienda] : [];

            if (!currentCities.includes(city)) {
                // Was added -> Add all stores
                const cityStores = storesInRegion[city] || [];
                newStores = [...new Set([...newStores, ...cityStores])];
            } else {
                // Was removed -> Remove all stores of this city
                const cityStores = storesInRegion[city] || [];
                newStores = newStores.filter(s => !cityStores.includes(s));
            }

            return {
                ...prev,
                segmentacion: {
                    ...prev.segmentacion,
                    ciudad: newCities,
                    tienda: newStores
                }
            };
        });
    };

    const handleStoreToggle = (store) => {
        setFormData(prev => {
            const currentStores = Array.isArray(prev.segmentacion.tienda) ? prev.segmentacion.tienda : [];
            const newStores = currentStores.includes(store)
                ? currentStores.filter(s => s !== store)
                : [...currentStores, store];

            return {
                ...prev,
                segmentacion: {
                    ...prev.segmentacion,
                    tienda: newStores
                }
            };
        });
    };

    // --- DRAG AND DROP HANDLERS ---
    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setComerciales((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);

                // 1. Move Array locally
                const newOrder = arrayMove(items, oldIndex, newIndex);

                // 2. Persist new indices in background
                const updates = newOrder.map((item, index) => ({
                    id: item.id,
                    orderIndex: index
                }));

                // Call API (Batch update mock)
                // In a real scenario, you'd want a batch write. 
                // Here we'll just loop update for simplicity or use a specific service if available.
                console.log("Saving new order:", updates);

                // Fire and forget updates to stay responsive
                updates.forEach(u => {
                    updateComercial(u.id, { orderIndex: u.orderIndex }).catch(e => console.error("Order save fail", e));
                });

                // Update local items with new index immediately to keep UI in sync
                return newOrder.map((item, index) => ({ ...item, orderIndex: index }));
            });
        }
    };

    const calculateAnalytics = (comercial) => {
        if (!comercial || !comercial.duracion || comercial.duracion <= 0) return null;

        // 1. Calculate Daily Operating Window (Defensive Parsing)
        const rawStart = comercial.dailyStartTime || "08:00";
        const rawEnd = comercial.dailyEndTime || "20:00";
        const [startH, startM] = rawStart.split(':').map(n => parseInt(n, 10) || 0);
        const [endH, endM] = rawEnd.split(':').map(n => parseInt(n, 10) || 0);

        const dailyWindowHours = ((Number.isFinite(endH) ? endH : 20) + (endM || 0) / 60) -
            ((Number.isFinite(startH) ? startH : 8) + (startM || 0) / 60);
        const dailyWindowClamped = Math.max(0.1, Math.min(24, dailyWindowHours));

        // 2. Calculate Campaign Duration (Days) - Handle missing dates
        const start = new Date(comercial.scheduleStart || comercial.fechaRegistro || Date.now());
        const end = new Date(comercial.scheduleEnd || Date.now());
        const diffMs = isNaN(start.getTime()) || isNaN(end.getTime()) ? 0 : (end - start);
        const totalDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

        // 3. Loops and Metrics
        const loops1h = Math.floor(3600 / comercial.duracion);
        const loops1day = Math.floor((dailyWindowClamped * 3600) / comercial.duracion);
        const totalLoopsCampaign = loops1day * totalDays;

        // Projections
        const loops1week = loops1day * 7;
        const loops1month = loops1day * 30;

        const IMPACT_PER_LOOP = 30; // 30 potential views per loop
        const impactCampaign = totalLoopsCampaign * IMPACT_PER_LOOP;

        return {
            loops1h: loops1h || 0,
            loops1day: loops1day || 0,
            loops1week: loops1week || 0,
            loops1month: loops1month || 0,
            totalLoopsCampaign: totalLoopsCampaign || 0,
            dailyWindow: Number(dailyWindowClamped.toFixed(1)),
            totalDays: totalDays || 1,
            totalHoursCampaign: ((totalLoopsCampaign || 0) * comercial.duracion) / 3600
        };
    };

    const handleDownload = async (comercial) => {
        if (!comercial.videoUrl) return;

        try {
            console.log("[Dashboard] Initializing SDK download...");
            const blob = await downloadVideo(comercial.videoUrl);
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = comercial.fileName || `${comercial.titulo || 'video'}.mp4`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
            console.log("[Dashboard] Download successful.");
        } catch (error) {
            console.error("[Dashboard] Download failed, falling back to new tab:", error);
            // Fallback: open in new tab if SDK download fails
            window.open(comercial.videoUrl, '_blank');
        }
    };

    const handleDeleteRequest = (comercial) => {
        setVideoToDelete(comercial);
    };

    const confirmDelete = async () => {
        if (!videoToDelete) return;

        setIsSyncing(true); // Show loading indicator
        try {
            console.log("Starting deletion process for:", videoToDelete.titulo);

            // 1. Delete from Storage (if exists)
            if (videoToDelete.videoUrl) {
                await deleteVideoFile(videoToDelete.videoUrl).catch(err => console.warn("Storage delete partial fail:", err));
            }

            // 2. Delete from Firestore
            await deleteComercial(videoToDelete.id);

            // 3. Update Local State (Optimistic update)
            setComerciales(prev => prev.filter(c => c.id !== videoToDelete.id));

            console.log("Deletion complete.");
            setVideoToDelete(null); // Close modal
        } catch (error) {
            console.error("Delete failed:", error);
            if (error.code === 'permission-denied' || error.message?.includes('permission')) {
                alert("🛑 ERROR DE PERMISOS: No tienes permiso para borrar este video desde este dominio o sesión. Revisa las reglas de FIRESTORE (no Storage).");
            } else {
                alert("Error al eliminar el video: " + error.message);
            }
        } finally {
            setIsSyncing(false);
            console.log("[Dashboard] Deletion attempt finished.");
        }
    };

    const cancelDelete = () => {
        setVideoToDelete(null);
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const handleStatusToggle = async (comercialId) => {
        if (!canManageStatus) return;

        const comercial = comerciales.find(c => c.id === comercialId);
        if (!comercial) return;

        const nuevoEstado = comercial.estado === 'online' ? 'offline' : 'online';

        // 2. Optimistic UI update
        setComerciales(prev => prev.map(c => {
            if (c.id === comercialId) {
                return { ...c, estado: nuevoEstado };
            }
            return c;
        }));

        // 3. Persist to Firestore
        try {
            await updateComercial(comercialId, { estado: nuevoEstado });
            console.log(`[Dashboard] Status persisted for ${comercialId}: ${nuevoEstado}`);
        } catch (error) {
            console.error("[Dashboard] Failed to persist status change:", error);
            alert("Error al guardar el cambio de estado en la base de datos.");

            // 4. Rollback on failure
            setComerciales(prev => prev.map(c => {
                if (c.id === comercialId) {
                    return { ...c, estado: comercial.estado };
                }
                return c;
            }));
        }
    };

    const safeNewDate = (date) => {
        if (!date) return new Date();
        if (typeof date.toDate === 'function') return date.toDate();
        return new Date(date);
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        try {
            const d = safeNewDate(date);
            if (isNaN(d.getTime())) return "Fecha inválida";
            return d.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return "Error fecha";
        }
    };

    const formatTime = (date) => {
        if (!date) return "--:--";
        try {
            const d = safeNewDate(date);
            if (isNaN(d.getTime())) return "--:--";
            return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            return "--:--";
        }
    };

    const getSegmentationIcon = (segmentacion) => {
        // Handle object or string (legacy)
        const type = typeof segmentacion === 'object' ? segmentacion.tipo : segmentacion;

        const icons = {
            'Nacional': '🌎',
            'Por Zona': '📍',
            'Provincial': '🏛️',
            'Regional': '🗺️',
            'Local': '📍'
        };
        return icons[type] || '📺';
    };

    const getMonthYear = (date) => {
        try {
            const d = safeNewDate(date);
            if (isNaN(d.getTime())) return "Fecha desconocida";
            return d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
        } catch (e) {
            return "Fecha inválida";
        }
    };

    const getWeekRange = (date) => {
        try {
            const d = safeNewDate(date);
            if (isNaN(d.getTime())) return "Semana desconocida";
            const day = d.getDay();
            const diff = d.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(d.setDate(diff));
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);

            return `${monday.getDate()} ${monday.toLocaleDateString('es-ES', { month: 'short' })} - ${sunday.getDate()} ${sunday.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}`;
        } catch (e) {
            return "Rango inválido";
        }
    };

    // Filter videos only
    const filteredVideos = useMemo(() => {
        try {
            return (comerciales || []).filter(comercial => {
                const matchEstado = filters.estado === 'all' || comercial.estado === filters.estado;
                const matchOrientacion = filters.orientacion === 'all' || comercial.orientacion === filters.orientacion;
                return matchEstado && matchOrientacion;
            });
        } catch (e) {
            console.error("[Dashboard] Filter error:", e);
            return [];
        }
    }, [comerciales, filters]);

    const [testResult, setTestResult] = useState(null);
    const handleTestWrite = async () => {
        setTestResult("⏳ Probando...");
        try {
            const testDoc = await createComercial({
                titulo: "TEST_CONEXION",
                estado: "error_db", // Hide from list if it works
                test: true,
                usuario: user.name
            });
            await deleteComercial(testDoc.id);
            setTestResult("✅ ÉXITO: Escritura y Lectura OK");
            alert("¡Éxito! Tu navegador puede escribir y leer en Firestore. Si los videos no aparecen, revisa que no estés en el modo 'Filtros' o que la colección sea la correcta.");
        } catch (e) {
            console.error("Test Write Failure:", e);
            setTestResult(`❌ ERROR: ${e.code || e.message}`);
            alert(`Fallo de escritura: ${e.message}\n\nRevisa tus Reglas de Cloud Firestore (No Storage).`);
        }
    };

    return (
        <div className="dashboard-wrapper">
            {/* MOBILE ORIENTATION GUARD */}
            <div className="mobile-orientation-guard">
                <div className="guard-content">
                    <div className="rotate-icon">
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="rotate-svg">
                            <rect x="20" y="10" width="24" height="44" rx="3" stroke="#334155" strokeWidth="2.5" className="phone-rect" />
                            <path d="M32 14V14.5" stroke="#334155" strokeWidth="2" strokeLinecap="round" />
                            <path d="M32 50V50.5" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
                            {/* Curved Arrow */}
                            <path d="M50 22C50 22 55 26 55 32C55 38 50 42 50 42" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="arrow-path" />
                            <path d="M54 39L50 42L46 39" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="arrow-head" />
                        </svg>
                    </div>
                    <h2>Video Manager</h2>
                    <p>Te recomienda girar el dispositivo para mejorar tu experiencia.</p>
                </div>
            </div>

            <Navbar />

            <div className="dashboard-container">


                {showForm && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <button
                                className="modal-close-btn"
                                onClick={() => setShowForm(false)}
                                title="Cerrar formulario"
                            >
                                ✕
                            </button>

                            <div className="form-card" style={{ boxShadow: 'none', border: 'none', margin: 0 }}>
                                <h2>📹 Registrar Nuevo Comercial</h2>
                                <form onSubmit={handleSubmit} className="commercial-form">
                                    <div className="form-main-split">
                                        {/* Left Column: Upload */}
                                        <div className="form-split-left">
                                            <div className="form-group">
                                                <span className="upload-label-wrapper">
                                                    <span className="upload-label-text">Subir Video *</span>
                                                    <p className="upload-limit">Soporta Horizontal y Vertical (Máx. 500MB)</p>
                                                </span>
                                                <label className="file-upload-zone" htmlFor="archivo">
                                                    <input
                                                        type="file"
                                                        id="archivo"
                                                        onChange={handleFileChange}
                                                        accept="video/*"
                                                        required
                                                        className="file-input"
                                                    />
                                                    <span className="upload-icon">☁️</span>
                                                    <span className="upload-text">
                                                        {formData.archivo ? formData.archivo.name : 'Haz clic o arrastra tu video aquí'}
                                                    </span>

                                                    {formData.archivo && (
                                                        <div style={{ marginTop: '10px' }} className="count-badge">
                                                            {(formData.archivo.size / (1024 * 1024)).toFixed(2)} MB
                                                        </div>
                                                    )}

                                                    {uploadProgress > 0 && uploadProgress < 100 && (
                                                        <div className="progress-bar" style={{ width: '100%', marginTop: '15px' }}>
                                                            <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                                                        </div>
                                                    )}

                                                    {formData.videoPreview && (
                                                        <div className="video-preview-small">
                                                            <video controls src={formData.videoPreview} onClick={(e) => e.preventDefault()} />
                                                        </div>
                                                    )}
                                                </label>
                                            </div>
                                        </div>

                                        {/* Right Column: Details & Segmentation */}
                                        <div className="form-split-right">
                                            <div className="form-group">
                                                <label htmlFor="titulo">Título del Comercial *</label>
                                                <input
                                                    type="text"
                                                    id="titulo"
                                                    name="titulo"
                                                    value={formData.titulo}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                                                    placeholder="Esperando video para generar título..."
                                                    readOnly={!formData.archivo}
                                                    required
                                                    className={!formData.archivo ? 'input-readonly' : ''}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="propietario">Propietario</label>
                                                <select
                                                    name="propietario"
                                                    value={formData.propietario}
                                                    onChange={handleInputChange}
                                                    className="segment-select"
                                                >
                                                    <option value="Jaher">Jaher</option>
                                                    <option value="Marca Asociada">Marca Asociada</option>
                                                </select>
                                            </div>

                                            <div className="form-group">
                                                <label>Segmentación *</label>
                                                <div className="segmentation-controls">
                                                    <select
                                                        value={formData.segmentacion.tipo}
                                                        onChange={(e) => handleSegmentationChange('tipo', e.target.value)}
                                                        required
                                                        className="segment-select"
                                                    >
                                                        <option value="Nacional">🌎 Nacional (Todas)</option>
                                                        <option value="Por Zona">📍 Por Zona/Ciudad</option>
                                                    </select>

                                                    {formData.segmentacion.tipo === 'Por Zona' && (
                                                        <>
                                                            <div className="multi-select-container">
                                                                <label className="sub-label">Regiones:</label>
                                                                <div className="checkbox-list">
                                                                    {Object.keys(SEGMENTATION_DATA).map(region => (
                                                                        <label key={region} className="checkbox-item">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={(formData.segmentacion.region || []).includes(region)}
                                                                                onChange={() => handleRegionToggle(region)}
                                                                            />
                                                                            {region}
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Iterate over ALL selected regions to show cities */}
                                                            {formData.segmentacion.region && formData.segmentacion.region.length > 0 && (
                                                                <div className="multi-select-container">
                                                                    <label className="sub-label">Ciudades:</label>
                                                                    <div className="checkbox-list">
                                                                        {formData.segmentacion.region.map(region => {
                                                                            const cities = Object.keys(SEGMENTATION_DATA[region] || {});
                                                                            return (
                                                                                <div key={region} className="city-store-group">
                                                                                    <strong>{region}</strong>
                                                                                    {cities.map(city => (
                                                                                        <label key={city} className="checkbox-item">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={(formData.segmentacion.ciudad || []).includes(city)}
                                                                                                onChange={() => handleCityToggle(city)}
                                                                                            />
                                                                                            {city}
                                                                                        </label>
                                                                                    ))}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {formData.segmentacion.ciudad && formData.segmentacion.ciudad.length > 0 && (
                                                                <div className="multi-select-container">
                                                                    <label className="sub-label">Tiendas:</label>
                                                                    <div className="checkbox-list">
                                                                        {/* Show stores for ALL selected cities */}
                                                                        {formData.segmentacion.ciudad.map(city => {
                                                                            // Find which region this city belongs to, to get its stores
                                                                            let stores = [];
                                                                            const region = formData.segmentacion.region.find(r => SEGMENTATION_DATA[r] && SEGMENTATION_DATA[r][city]);
                                                                            if (region) {
                                                                                stores = SEGMENTATION_DATA[region][city] || [];
                                                                            }
                                                                            return (
                                                                                <div key={city} className="city-store-group">
                                                                                    <strong>{city}</strong>
                                                                                    {stores.map(store => (
                                                                                        <label key={store} className="checkbox-item xs">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={(formData.segmentacion.tienda || []).includes(store)}
                                                                                                onChange={() => handleStoreToggle(store)}
                                                                                            />
                                                                                            {store}
                                                                                        </label>
                                                                                    ))}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="orientacion">Orientación del Video</label>
                                            <div className={`orientation-badge ${formData.orientacion === 'Vertical' ? 'vertical' : 'horizontal'}`}>
                                                {formData.orientacion ? (formData.orientacion === 'Vertical' ? '📲 Vertical' : '📱 Horizontal') : 'Esperando video...'}
                                            </div>
                                            <input type="hidden" name="orientacion" value={formData.orientacion} />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="duracion">Duración (segundos)</label>
                                            <input
                                                type="number"
                                                id="duracion"
                                                name="duracion"
                                                value={formData.duracion}
                                                readOnly
                                                className="input-readonly"
                                                placeholder="Automático"
                                            />
                                            <small className="help-text">Se detecta automáticamente al subir video</small>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Programación Inicio (Fecha y Hora) *</label>
                                            <DatePicker
                                                selected={formData.scheduleStart}
                                                onChange={(date) => setFormData(prev => ({ ...prev, scheduleStart: date }))}
                                                showTimeSelect
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                timeFormat="HH:mm"
                                                className="date-input"
                                                minDate={new Date()}
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>Programación Fin (Fecha y Hora) *</label>
                                            <DatePicker
                                                selected={formData.scheduleEnd}
                                                onChange={(date) => setFormData(prev => ({ ...prev, scheduleEnd: date }))}
                                                showTimeSelect
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                timeFormat="HH:mm"
                                                className="date-input"
                                                minDate={formData.scheduleStart}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Daily Time Removed as requested */}

                                    <div className="form-row">
                                        <div className="form-group full-width">
                                            <label>Comentarios / Notas</label>
                                            <textarea
                                                value={formData.comentarios}
                                                onChange={(e) => setFormData(prev => ({ ...prev, comentarios: e.target.value }))}
                                                placeholder="Comentarios adicionales..."
                                                rows="3"
                                                className="form-textarea"
                                            />
                                        </div>
                                    </div>

                                    {canManageStatus && (
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="estado">Estado *</label>
                                                <select
                                                    id="estado"
                                                    name="estado"
                                                    value={formData.estado}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="offline">⭕ Offline</option>
                                                    <option value="online">✅ Online</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    <button type="submit" className="btn-submit" disabled={uploadProgress > 0}>
                                        {uploadProgress > 0
                                            ? (uploadProgress >= 100 ? '⌛ Finalizando publicación...' : `Subiendo Video... ${Math.round(uploadProgress)}%`)
                                            : '✓ Registrar Comercial'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                <AnalyticsPanel comerciales={comerciales} />

                <div className="commercials-section">
                    <div className="section-header elite-header">
                        <div className="header-left">
                            <h2>📺 Comerciales Registrados</h2>
                            <span className="count-badge">{comerciales.length}</span>
                        </div>

                        <div className="header-actions-group">
                            <button
                                className="btn-action btn-action-success"
                                onClick={() => {
                                    setUploadProgress(0);
                                    setShowForm(!showForm);
                                }}
                            >
                                {showForm ? '✕ Cancelar' : '+ Nuevo Comercial'}
                            </button>

                            <button
                                className="btn-action preview-all-btn"
                                onClick={() => setShowGlobalPreview(true)}
                                title="Reproducir todos los videos en secuencia a velocidad x3"
                            >
                                Ver Bucle General
                            </button>

                            {/* BULK DELETE BUTTON - MODE SWITCHER */}
                            {!isSelectionMode ? (
                                <button
                                    className="btn-action btn-danger-soft"
                                    onClick={() => setIsSelectionMode(true)}
                                    title="Activar casillas para seleccionar múltiples videos"
                                >
                                    🗑️ Eliminar Varios
                                </button>
                            ) : (
                                <>
                                    <button
                                        className="btn-action btn-secondary-soft"
                                        onClick={() => {
                                            setIsSelectionMode(false);
                                            setSelectedVideos(new Set());
                                        }}
                                        title="Cancelar selección"
                                    >
                                        ✕ Cancelar
                                    </button>

                                    <button
                                        className={`btn-action btn-danger ${selectedVideos.size === 0 ? 'disabled' : ''}`}
                                        onClick={handleBulkDelete}
                                        disabled={selectedVideos.size === 0}
                                        title="Eliminar permanentemente"
                                    >
                                        🗑️ Confirmar Borrado ({selectedVideos.size})
                                    </button>
                                </>
                            )}
                        </div>

                        {isAdmin && comerciales.length > 0 && (
                            <div className="filter-controls">
                                <div className="filter-group">
                                    <label>Estado:</label>
                                    <select
                                        value={filters.estado}
                                        onChange={(e) => handleFilterChange('estado', e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="all">Todos</option>
                                        <option value="online">✅ Online</option>
                                        <option value="offline">⭕ Offline</option>
                                    </select>
                                </div>

                                <div className="filter-group">
                                    <label>Orientación:</label>
                                    <select
                                        value={filters.orientacion}
                                        onChange={(e) => handleFilterChange('orientacion', e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="all">Todas</option>
                                        <option value="Horizontal">📱 Horizontal</option>
                                        <option value="Vertical">📲 Vertical</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {comerciales.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">📹</div>
                            <h3>No hay comerciales registrados</h3>
                            <p>{!isAdmin ? 'Haz clic en "Nuevo Comercial" para agregar tu primer video' : 'Esperando que los usuarios suban videos'}</p>
                        </div>
                    ) : filteredVideos.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">🔍</div>
                            <h3>Sin resultados</h3>
                            <p>No hay videos que coincidan con los filtros actuales.</p>
                        </div>
                    ) : (
                        <div className="dashboard-content-split">
                            {/* LEFT: Video List (75%) */}
                            <div className="video-list-container">
                                {/* BULK ACTIONS BAR */}
                                {selectedVideos.size > 0 && (
                                    <div className="bulk-actions-bar">
                                        <div className="bulk-info">
                                            <input
                                                type="checkbox"
                                                checked={selectedVideos.size === filteredVideos.length}
                                                onChange={toggleAllVideos}
                                                className="row-selection-checkbox"
                                            />
                                            <span>{selectedVideos.size} seleccionados</span>
                                        </div>
                                        <button onClick={handleBulkDelete} className="btn-bulk-delete">
                                            🗑️ Eliminar Seleccionados
                                        </button>
                                    </div>
                                )}

                                <div className="videos-list">
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext
                                            items={filteredVideos}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {filteredVideos.map((comercial, index) => (
                                                <SortableRow
                                                    key={comercial.id}
                                                    comercial={comercial}
                                                    index={index}
                                                    selectedAnalyticsIds={selectedAnalyticsIds}
                                                    toggleAnalyticsSelection={toggleAnalyticsSelection}
                                                    isSelected={selectedVideos.has(comercial.id)}
                                                    toggleSelection={toggleVideoSelection}
                                                    handleDownload={handleDownload}
                                                    handleDuplicate={handleDuplicate}
                                                    handleDeleteRequest={handleDeleteRequest}
                                                    handleRequestDelete={handleRequestDelete}
                                                    handleStatusToggle={handleStatusToggle}
                                                    isAdmin={isAdmin}
                                                    canManageStatus={canManageStatus}
                                                    isSelectionMode={isSelectionMode}
                                                />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            </div >

                            {/* RIGHT: Analytics Sidebar (25%) */}
                            < div className="analytics-sidebar" >
                                <div className="sidebar-header">
                                    <h3>📊 Comparativa ({selectedAnalyticsIds.size})</h3>
                                    {selectedAnalyticsIds.size > 0 && (
                                        <button onClick={() => setSelectedAnalyticsIds(new Set())} className="btn-clear-all">
                                            Limpiar
                                        </button>
                                    )}
                                </div>
                                <div className="sidebar-content">
                                    {selectedAnalyticsIds.size === 0 ? (
                                        <div className="sidebar-placeholder">
                                            <p>Selecciona videos de la lista para ver su analítica detallada y compararlos.</p>
                                        </div>
                                    ) : (
                                        comerciales
                                            .filter(c => selectedAnalyticsIds.has(c.id))
                                            .map(comercial => {
                                                const realTime = (() => {
                                                    const now = new Date();
                                                    const reg = new Date(comercial.fechaRegistro);
                                                    const diffMs = now - reg;
                                                    // Simple estimation: diffMs / 1000 / duration 
                                                    // But limited by operating hours? keeping it distinct from "Potential"
                                                    const elapsedSeconds = Math.max(0, diffMs / 1000);
                                                    return Math.floor(elapsedSeconds / comercial.duracion);
                                                })();
                                                // Calculate precise stats for this specific video
                                                const stats = calculateAnalytics(comercial);

                                                return (
                                                    <div key={comercial.id} className="analytics-card-item">
                                                        <div className="analytics-card-header">
                                                            <div className="mini-thumb">
                                                                {comercial.videoPreview && <video src={comercial.videoPreview} />}
                                                            </div>
                                                            <div className="mini-info">
                                                                <h4>{comercial.titulo}</h4>
                                                                <span>{comercial.duracion}s | {comercial.fileSize}MB</span>
                                                            </div>
                                                            <button
                                                                className="btn-remove-item"
                                                                onClick={() => toggleAnalyticsSelection(comercial.id)}
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                        <div className="analytics-metrics-grid">
                                                            <div className="metric-box">
                                                                <span className="metric-label">Horario Diario</span>
                                                                <span className="metric-value current">{stats ? stats.dailyWindow : 0}h</span>
                                                            </div>
                                                            <div className="metric-box">
                                                                <span className="metric-label">Vigencia</span>
                                                                <span className="metric-value">{stats ? stats.totalDays : 0} días</span>
                                                            </div>
                                                            <div className="metric-box highlight">
                                                                <span className="metric-label">Ciclos Totales</span>
                                                                <span className="metric-value">{(stats?.totalLoopsCampaign || 0).toLocaleString()}</span>
                                                            </div>
                                                            <div className="metric-box">
                                                                <span className="metric-label">Ciclos Semanales</span>
                                                                <span className="metric-value">{(stats?.loops1week || 0).toLocaleString()}</span>
                                                            </div>
                                                        </div>
                                                        {/* Visual Bar Graph */}
                                                        <div className="metric-graph-container">
                                                            <div className="graph-label">Distribución Mensual</div>
                                                            <div className="graph-bar-bg">
                                                                <div
                                                                    className="graph-bar-fill"
                                                                    style={{ width: `${Math.min(100, ((stats?.totalLoopsCampaign || 0) / 100000) * 100)}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                    )}

                                    {/* TOTALS CARD */}
                                    {selectedAnalyticsIds.size > 0 && (() => {
                                        const selectedVideos = comerciales.filter(c => selectedAnalyticsIds.has(c.id));
                                        const totals = selectedVideos.reduce((acc, video) => {
                                            const stats = calculateAnalytics(video);
                                            if (stats) {
                                                acc.loops += (stats.totalLoopsCampaign || 0);
                                                acc.weeklyLoops += (stats.loops1week || 0);
                                                acc.hrs += (stats.totalHoursCampaign || 0);
                                            }
                                            return acc;
                                        }, { loops: 0, weeklyLoops: 0, hrs: 0 });

                                        return (
                                            <div className="analytics-total-card">
                                                <div className="total-header">
                                                    <h4><span>📈</span> PROYECCIÓN ACUMULADA</h4>
                                                </div>
                                                <div className="total-metrics-grid">
                                                    <div className="total-metric highlight">
                                                        <span className="label">Total Reproducciones</span>
                                                        <span className="value">{(totals.loops || 0).toLocaleString()}</span>
                                                    </div>
                                                    <div className="total-metric">
                                                        <span className="label">Total Proyección Semanal</span>
                                                        <span className="value">{(totals.weeklyLoops || 0).toLocaleString()}</span>
                                                    </div>
                                                    <div className="total-metric">
                                                        <span className="label">Total Horas Aire</span>
                                                        <span className="value">{Math.round(totals.hrs || 0).toLocaleString()}h</span>
                                                    </div>
                                                </div>
                                                <p className="total-disclaimer">* Basado en programación y horarios seleccionados.</p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div >
                        </div >
                    )}
                </div >
            </div >

            {/* Global Preview Overlay */}
            {showGlobalPreview && (
                <GlobalPreviewOverlay
                    videos={filteredVideos}
                    onClose={() => setShowGlobalPreview(false)}
                />
            )}

            <Footer />

            {/* Delete Confirmation Modal */}
            {
                videoToDelete && (
                    <div className="delete-modal-overlay">
                        <div className="delete-modal-content">
                            <h3>¿Eliminar Video?</h3>
                            <p>Estás a punto de eliminar <strong>{videoToDelete.titulo}</strong>.</p>
                            <p className="delete-warning">Esta acción no se puede deshacer.</p>
                            <div className="modal-actions">
                                <button className="btn-cancel" onClick={cancelDelete}>Cancelar</button>
                                <button className="btn-delete-confirm" onClick={confirmDelete}>Sí, eliminar</button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default Dashboard;



